// Regex pattern to match chord notation
// Matches patterns like: C, Dm, F#, Bb, Am7, Csus4, etc.
//
// A trailing inversion mark ('C^1') ends the chord without being part of it: the mark is added to
// the lookahead rather than to the body so transposing rewrites the note and leaves the mark where
// it is, which is exactly what it means — how the chord is turned over doesn't change with the key.
export const CHORD_REGEX = /([A-G](?:#|b)?(?:m|min)?(?:sus|aug|dim|add|M)?\d*)(?=$|\s|\/|-|\r|\^)(?! [a-z])/g;

// Display regex — same as CHORD_REGEX but captures slash chords (e.g. G/E) and inversion marks
// (e.g. C^1) as a single token, so the chord piano can show the bass key and a marked chord is drawn
// as one symbol. Used only for rendering, NOT for transposing (lyricsActions still uses CHORD_REGEX
// so both notes are transposed separately).
export const DISPLAY_CHORD_REGEX =
  /([A-G](?:#|b)?(?:m|min)?(?:sus|aug|dim|add|M)?\d*(?:\/[A-G](?:#|b)?)?(?:\^[1-3])?)(?=$|\s|\/|-|\r)(?! [a-z])/g;

// Everything outside this file goes through the functions below rather than the regexes above, so
// every caller agrees on which lines are chord lines.

const LINE_BREAK = /(\r\n|\r|\n)/;
const regexFor = (display) => new RegExp(display ? DISPLAY_CHORD_REGEX : CHORD_REGEX);

// A single line, no line breaks
export const isChordLine = (line) => regexFor(false).test(line);

export const isLyricsLine = (line) => line.trim().length > 0 && !isChordLine(line);

// Runs fn per line, leaving the line breaks between them exactly as written
const mapLines = (text, fn) =>
  String(text ?? '')
    .split(LINE_BREAK)
    .map((piece, i) => (i % 2 === 1 ? piece : fn(piece)))
    .join('');

/**
 * Every chord in the text, line by line, skipping lyric lines. `display` reads slash chords and
 * inversion marks as one symbol ("G/E", "C^1"), the way they are drawn; without it they come apart
 * into the notes a transpose moves.
 */
export const findChords = (text, { display = false } = {}) =>
  String(text ?? '')
    .split(LINE_BREAK)
    .filter((line, i) => i % 2 === 0 && isChordLine(line))
    .flatMap((line) => line.match(regexFor(display)) || []);

export const hasChords = (text) => findChords(text).length > 0;

// text.replace over the chords only, with the same line rules and `display` option as findChords
export const replaceChords = (text, fn, { display = false } = {}) =>
  mapLines(text, (line) => (isChordLine(line) ? line.replace(regexFor(display), fn) : line));

/**
 * Bars on a chord line written with bar lines: "| Dm | Em C | B |" is three bars.
 *
 * Returns null when the line carries no bar line at all — the signal to fall back to counting
 * chords, since a bare "Dm Em C B" is four bars of one chord each, not one bar of four.
 *
 * Outer bar lines are optional, so an empty segment at either end is dropped and "Dm | B" counts
 * the same as "| Dm | B |". An empty segment *between* two bar lines is kept, because that is how
 * a chart says "hold it": "| Dm | | B |" is three bars, the middle one still on the Dm. Counting
 * segments rather than chords is the whole point — it reads both a chord held over several bars
 * and several chords sharing one, neither of which findChords can see.
 */
export const countBars = (text) => {
  const line = String(text ?? '');
  if (!line.includes('|')) return null;

  const segments = line.split('|');
  if (segments[0].trim() === '') segments.shift();
  if (segments.length > 0 && segments[segments.length - 1].trim() === '') segments.pop();

  return segments.length;
};
